import { clearProductTimeout, setProductTimeout } from "@/utils/productTimers";

/**
 * The citation deep link the backend writes into a delivered document.
 *
 * `sanitize_html` (fp/processing/reports.py) mints every citation in an
 * exported document as an absolute anchor —
 * `<workspace>/chat/<uuid>?turn=…&cid=…&match=…` (#11608; documents shipped
 * before that carry `?qid=…` with the full prefixed id, and Reports exports
 * still do) — because the file is a deliverable that has to resolve when it is
 * opened outside the app. That is also why nothing here rewrites the link: the
 * URL stays exactly as shipped, and only a click that happens *inside* the app
 * is intercepted (#11164).
 *
 * The field names match the `openDocumentPanel` detail the chat renderer and
 * the post-reload landing effect already dispatch, so a parsed link can be
 * handed straight to the viewer.
 */
export interface CitationDeepLink {
  citationId: string;
  queryId: string;
  originalNumValue: string | undefined;
  /** The passage line the link's anchor sits on (`ctx`, #14039): the viewer
   *  windows its search for `originalNumValue` on it, so a figure the page
   *  repeats lands on the cited occurrence. Absent on every link minted
   *  before it existed, which then anchors on the figure as before. */
  linkContext?: string;
}

// The two citation-scope namespaces, owned here so the slug↔id conversion has
// one home on this side of the wire (mirroring `source_maps.py`, which owns it
// on the backend). `chatUtils.ts` imports the chat prefix for its turn-anchor
// helpers rather than restating it.
export const CHAT_TURN_ID_PREFIX = "pgid-chat-turns-";
export const AGENT_SESSION_ID_PREFIX = "pgid-agent-sessions-";

/**
 * A `turn=` value expanded back to the id the resolution endpoint dispatches
 * on. Delivered documents carry the bare id — internal `pgid-` prefixes never
 * belong in a user-visible URL (#11608) — so the namespace is restored here:
 * from the link's own path when a full href is available, or from the landing
 * surface's scope when only a search string is. An already-prefixed value
 * passes through so a hand-edited or historical link cannot double-prefix.
 */
const expandTurnSlug = (slug: string, scopePrefix: string): string =>
  slug.startsWith("pgid-") ? slug : `${scopePrefix}${slug}`;

/**
 * The citation a link points at, or null when the href is not a citation deep
 * link and should keep its ordinary navigating behavior.
 *
 * Both ids are required. `match` is optional: `sanitize_html` emits `&match=`
 * with an empty value whenever the citation carries no matched figure, and an
 * empty string would send the viewer looking for a highlight that isn't there
 * — so it is normalized to undefined, matching what the deep-link landing in
 * `AgenticChatConversation` does with the same parameter.
 *
 * The query string is parsed rather than substring-matched so a parameter that
 * merely ends in `cid` — `docid=` — is not misread as a citation.
 */
export const parseCitationDeepLink = (
  href: string
): CitationDeepLink | null => {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  const citationId = url.searchParams.get("cid");
  const turnSlug = url.searchParams.get("turn");
  const queryId = turnSlug
    ? expandTurnSlug(
        turnSlug,
        url.pathname.includes("/agents/")
          ? AGENT_SESSION_ID_PREFIX
          : CHAT_TURN_ID_PREFIX
      )
    : url.searchParams.get("qid");
  if (!citationId || !queryId) {
    return null;
  }

  // `ctx` is optional like `match`, and an empty one is absent: the viewer
  // would otherwise try to locate an empty passage (#14039).
  const linkContext = url.searchParams.get("ctx") || undefined;
  return {
    citationId,
    queryId,
    originalNumValue: url.searchParams.get("match") || undefined,
    ...(linkContext ? { linkContext } : {}),
  };
};

/**
 * `href` without its `ctx` parameter, every other part as written.
 *
 * A figure link's `ctx` is up to 160 characters of the cited passage's text
 * (#14039). It is for the viewer inside the app; an href that leaves the app
 * (a Word export, an answer copied to the clipboard) keeps `qid`, `cid` and
 * `match`, which are what the landing resolves, and not the source's text.
 */
export const withoutLinkContext = (href: string): string => {
  const queryAt = href.indexOf("?");
  if (queryAt === -1) return href;
  const hashAt = href.indexOf("#", queryAt);
  const queryEnd = hashAt === -1 ? href.length : hashAt;
  const params = href.slice(queryAt + 1, queryEnd).split("&");
  const kept = params.filter((param) => !param.startsWith("ctx="));
  if (kept.length === params.length) return href;
  return (
    href.slice(0, queryAt) +
    (kept.length ? `?${kept.join("&")}` : "") +
    href.slice(queryEnd)
  );
};

/**
 * The citation a click landed on, or null when it missed one.
 *
 * The click target is routinely a descendant of the anchor rather than the
 * anchor itself — pdf.js paints its link annotations with the text in a child
 * element — so the enclosing anchor is what gets read.
 */
export const citationFromClickTarget = (
  target: EventTarget | null
): CitationDeepLink | null => {
  const anchor = (target as Element | null)?.closest?.("a");
  if (!anchor) {
    return null;
  }
  return parseCitationDeepLink(anchor.getAttribute("href") ?? "");
};

/**
 * A citation plus the viewer placement the surface it was clicked on needs.
 *
 * `inlineViewerOnly` is the one thing a surface may add: a page that draws its
 * own `<FileViewer />` (named agents' split pane) wants the shared viewer state
 * filled in without the global viewer mounting on top of it. It says nothing
 * about how the citation resolves, which is the backend's job everywhere.
 */
export type CitationOpenRequest = CitationDeepLink & {
  inlineViewerOnly?: boolean;
};

/**
 * Ask the app to open a citation's source in the right-side document panel.
 *
 * This is the same event, with the same payload, that the chat renderer and
 * the post-reload `?qid=&cid=` landing already dispatch — so every path that
 * resolves a citation resolves it identically.
 */
export const openCitationInDocumentPanel = (
  citation: CitationOpenRequest
): void => {
  window.dispatchEvent(
    new CustomEvent("openDocumentPanel", { detail: citation })
  );
};

/**
 * The citation a page was *opened on*, read off its own query string.
 *
 * The same three parameters as a deep-link href, and — since the exporter
 * stamps a resolvable turn id into every surface's links — the same
 * requirement that both ids be present. Neither surface resolves a marker
 * itself: a page hands the unresolved citation over and the backend answers it
 * against the turn's full citation registry, which includes markers that only
 * ever appear inside a delivered document and never in the answer text.
 */
export const citationFromSearch = (
  search: string | URLSearchParams,
  fallbackQueryId?: string,
  turnScopePrefix: string = CHAT_TURN_ID_PREFIX
): CitationDeepLink | null => {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  const citationId = params.get("cid");
  // A search string carries no path, so a `turn=` slug takes its namespace
  // from the landing surface via `turnScopePrefix` — the agents page names
  // its own namespace explicitly; only the chat surface omits it.
  const turnSlug = params.get("turn");
  // `fallbackQueryId` is for a surface that can name the turn from its own
  // route: documents delivered before the exporter stamped a resolvable id
  // carry no usable `qid`, and dropping them would leave every historical
  // deliverable's links opening nothing.
  const queryId = turnSlug
    ? expandTurnSlug(turnSlug, turnScopePrefix)
    : params.get("qid") || fallbackQueryId;
  if (!citationId || !queryId) {
    return null;
  }
  // The passage line the link's anchor sits on (#14039), read as the click
  // path reads it: a landing opened from a figure link (a new tab, a shared
  // URL) highlights the same occurrence the click would. Empty is absent.
  const linkContext = params.get("ctx") || undefined;
  return {
    citationId,
    queryId,
    // `sanitize_html` emits `&match=` with an empty value when the citation
    // carries no matched figure; an empty string would send the viewer looking
    // for a highlight that isn't there.
    originalNumValue: params.get("match") || undefined,
    ...(linkContext ? { linkContext } : {}),
  };
};

/**
 * How long to let a conversation settle before opening a landed citation.
 *
 * The page is still mounting when the effect fires — the transcript is
 * rendering, the viewer is not listening yet — so the dispatch waits. One
 * constant for every surface: the wait exists because of React mount ordering,
 * which is not a per-surface property, and two hand-picked numbers only made
 * the slower surface's races invisible on the faster one.
 */
export const CITATION_SETTLE_MS = 1000;

/**
 * The `qid` a named-agent session's citations resolve against.
 *
 * Mirrors `citation_scope_id` in `fp/agent/conversation/source_maps.py`, the
 * only other place this namespace is written. It is needed on this side
 * because deliverables shipped before the exporter stamped it carry the bare
 * session id (or nothing), which the resolution endpoint cannot dispatch on —
 * and the page landing on such a link knows its own session from the route
 * whatever the file says.
 */
export const agentSessionCitationScope = (sessionId: string): string =>
  `${AGENT_SESSION_ID_PREFIX}${sessionId}`;

/**
 * Dispatch an `openDocumentPanel` detail once the page has settled. Returns
 * the cleanup that cancels a pending open, for use straight from an effect.
 *
 * The detail is the UNRESOLVED citation, optionally carrying the viewer flags
 * the landing surface needs (a page that draws its own split pane asks for
 * `inlineViewerOnly`). Resolution itself belongs to the layout for every
 * surface, because only the backend can see the turn's whole registry.
 */
export const openDocumentPanelWhenSettled = (
  detail: CitationOpenRequest
): (() => void) => {
  const timeoutId = setProductTimeout(() => {
    window.dispatchEvent(new CustomEvent("openDocumentPanel", { detail }));
  }, CITATION_SETTLE_MS);
  return () => clearProductTimeout(timeoutId);
};
